/**
 * The shared foundry knowledge (L3, migration 0197). With the org's consent each org's pipeline writes generic
 * knowledge candidates: an approved word no global alias folds, a defect word with no home, a parameter an approved
 * extractor placed as NEW, and, with its consent to counts, counts (a hypothesis's verdict in its data, a tried
 * recommendation's effect). Never an org's figures or name. Only an org with real plant data contributes, and nothing
 * leaves an org until its admin consents: both switches default off.
 *
 *  - `/v1/admin/knowledge-candidates`: the platform console's curation (platform admins): the list (paged) carries
 *    counts of plants only; one candidate's detail names the orgs behind it (the console alone), keeps the history of
 *    every decision and drafts the pack change as text for a person's PR. Nothing writes the pack.
 *  - `/v1/twin/knowledge-sharing`: an org's own part, for every org (no pack gate): its consent to share counts and
 *    its consent to share content (`?setting=counts|content`; org admins give or withdraw them) and how many
 *    candidates of each kind it contributes to. The admin page's Privacy tab is its view.
 */

import { api } from "@/lib/api-client";

export type KnowledgeCandidateKind =
  | "alias"
  | "taxonomy_entry"
  | "catalog_parameter"
  | "recording_text"
  | "playbook_condition"
  | "hypothesis_verdict"
  | "recommendation_effect";

export type KnowledgeCandidateStatus = "open" | "accepted" | "rejected" | "landed";

/** One candidate as the console lists it (mirrors candidates.CandidateView): no org's name. */
export interface KnowledgeCandidate {
  id: string;
  kind: KnowledgeCandidateKind;
  /** The generic statement: pack ids, canonical names, a plant's word. Never a figure. */
  statement: Record<string, string>;
  summary: string;
  /** Distinct orgs contributing it now. */
  plants: number;
  /** Plants it needs to shape the pack: 3 for every kind, content included. */
  min_plants: number;
  shapes_pack: boolean;
  /** Whether it shapes the pack, in words. */
  shaping: string;
  first_seen_at: string;
  last_seen_at: string;
  status: KnowledgeCandidateStatus;
  pr_url?: string;
  decision_note?: string;
  decided_by?: string;
  decided_at?: string;
}

/** An org behind a candidate: named in the console's detail, and in no other response. */
export interface KnowledgeCandidateOrg {
  org_id: string;
  name: string;
  contributed_at: string;
}

/** One entry of a candidate's history: what a curator decided, the status it set, the note and PR it carried. */
export interface KnowledgeCandidateDecision {
  /** reopen is the system's: a withdrawal left an accepted candidate below its minimum of plants. */
  action: "accept" | "reject" | "landed" | "reopen";
  status: "open" | "accepted" | "rejected" | "landed";
  note?: string;
  pr_url?: string;
  /** The deciding platform admin's email (or id); empty for the system. */
  by: string;
  at: string;
}

export interface KnowledgeCandidateDetail extends KnowledgeCandidate {
  orgs: KnowledgeCandidateOrg[];
  /** For a count: the plants behind each verdict on the same hypothesis or recommendation. */
  verdict_counts?: Record<string, number>;
  /** Every decision taken on it, oldest first; the decision fields above hold the latest. */
  decisions: KnowledgeCandidateDecision[];
  /** The pack change it drafts, as text, for a person to copy into a PR. */
  draft: string;
}

/** One page of the list: `total` counts every candidate matching the filters, `next_offset` is null on the last page. */
export interface KnowledgeCandidateList {
  items: KnowledgeCandidate[];
  total: number;
  limit: number;
  offset: number;
  next_offset: number | null;
  min_plants_to_shape: number;
}

export interface KnowledgeCandidateFilter {
  status?: KnowledgeCandidateStatus | "";
  kind?: KnowledgeCandidateKind | "";
  limit?: number;
  offset?: number;
}

/** The org's own part in the shared knowledge (GET /v1/twin/knowledge-sharing). */
export interface KnowledgeSharing {
  /** The consent to contribute counts; default false. */
  share_knowledge_counts: boolean;
  /** The consent to contribute its words, names and parameters; default false. */
  share_knowledge_content: boolean;
  /** Plants a count needs before it shapes the pack (3). */
  min_plants_to_shape: number;
  /** Plants a word, a name or a parameter needs before it shapes the pack (3). */
  min_plants_content: number;
}

const BASE = "/v1/admin/knowledge-candidates";

/** The list's query string: only the filters and the page set (an offset of 0 is the first page). */
export function knowledgeCandidatesQuery(filter: KnowledgeCandidateFilter): string {
  const q = new URLSearchParams();
  if (filter.status) q.set("status", filter.status);
  if (filter.kind) q.set("kind", filter.kind);
  if (filter.limit) q.set("limit", String(filter.limit));
  if (filter.offset) q.set("offset", String(filter.offset));
  const s = q.toString();
  return s ? `?${s}` : "";
}

export const knowledgeCandidatesApi = {
  list: (filter: KnowledgeCandidateFilter = {}) =>
    api.get<KnowledgeCandidateList>(`${BASE}${knowledgeCandidatesQuery(filter)}`),
  get: (id: string) => api.get<KnowledgeCandidateDetail>(`${BASE}/${encodeURIComponent(id)}`),
  /** Open or rejected to accepted; refused (409 below_plant_threshold) for a count fewer than 3 plants contribute. */
  accept: (id: string, note = "") =>
    api.post<KnowledgeCandidateDetail>(`${BASE}/${encodeURIComponent(id)}/accept`, note ? { note } : {}),
  /** Open or accepted to rejected: the note says why. */
  reject: (id: string, note: string) =>
    api.post<KnowledgeCandidateDetail>(`${BASE}/${encodeURIComponent(id)}/reject`, { note }),
  /** Accepted to landed, with the https link of the PR that changed the pack. */
  land: (id: string, prUrl: string, note = "") =>
    api.post<KnowledgeCandidateDetail>(
      `${BASE}/${encodeURIComponent(id)}/landed`,
      note ? { pr_url: prUrl, note } : { pr_url: prUrl },
    ),
};

/** Which sharing setting a call sets: the consent to counts (the default) or the consent to content. */
export type KnowledgeSharingSetting = "counts" | "content";

const SHARING = "/v1/twin/knowledge-sharing";
const sharingUrl = (setting: KnowledgeSharingSetting) => (setting === "content" ? `${SHARING}?setting=content` : SHARING);

export const knowledgeSharingApi = {
  get: () => api.get<KnowledgeSharing>(SHARING),
  /** Org admins: consent to share counts, or (content) to share words, names and parameters (audited; a demo or
   *  rehearsal org is refused with 409 not_a_plant). */
  enable: (setting: KnowledgeSharingSetting = "counts") => api.post<KnowledgeSharing>(sharingUrl(setting)),
  /** Org admins: withdraw the consent, and every count the org contributed with it; or (content) withdraw the consent
   *  to share words, names and parameters, which withdraws the ones contributed (audited). */
  disable: (setting: KnowledgeSharingSetting = "counts") => api.delete<KnowledgeSharing>(sharingUrl(setting)),
};
